import { IUser } from '@common/types/user';
import {
    EventLevelBadgeRequirement,
    EventLevelBadgeRewards,
    IEventPermissions,
    IEventStatistics,
    ManagementOnSiteEvent,
    ManagementWebinarEvent,
    SignInGuestDialogState,
} from '@event/types/event';
import { createContext, useContext } from 'react';

type EventDashboardContextType = {
    user?: Omit<IUser, 'full_name'>;
    event?: ManagementOnSiteEvent | ManagementWebinarEvent;
    cohostPermissions?: IEventPermissions[];
    isEdit?: boolean;
    isEventFetching?: boolean;
    committedPublishedState?: boolean;
    statistics?: { data?: IEventStatistics; isFetching: boolean; isLoading: boolean };
    badges?: {
        required: { data?: EventLevelBadgeRequirement[]; isLoading: boolean };
        rewarded: { data?: EventLevelBadgeRewards[]; isLoading: boolean };
    };
    accordionsState: [
        Record<
            'analytics' | 'cohost' | 'details' | 'feedback' | 'registration_details' | 'badges' | 'audience',
            boolean
        >,
        React.Dispatch<
            React.SetStateAction<
                Record<
                    'analytics' | 'cohost' | 'details' | 'feedback' | 'registration_details' | 'badges' | 'audience',
                    boolean
                >
            >
        >
    ];
    attendeesTabState: [number, React.Dispatch<React.SetStateAction<number>>];
    activeAudienceFiltersState: [
        Record<string, string[]>,
        React.Dispatch<React.SetStateAction<Record<string, string[]>>>
    ];
    isOrganizer?: boolean;
    isCohost?: boolean;
    isUserSalesOrAdmin?: boolean;
    isOwnerOrAnyOrganizersNotSalesOrAdmin?: boolean;
    isAdmin?: boolean;
    signInGuestDialogState: [SignInGuestDialogState, React.Dispatch<React.SetStateAction<SignInGuestDialogState>>];
    tabState: [number, React.Dispatch<React.SetStateAction<number>>];
};

const EventDashboardContext = createContext<EventDashboardContextType | undefined>(undefined);

const useEventDashboardContext = () => {
    const context = useContext(EventDashboardContext);
    if (!context) throw new Error('useEventDashboardContext must be used within a provider');
    return context;
};

export { EventDashboardContext, useEventDashboardContext };
export type { EventDashboardContextType };
